import { useState, useEffect, useCallback } from 'react';
import { api, PendingOffer } from '../api/client';
import RefuseModal from '../components/RefuseModal';
import ConfirmModal from '../components/ConfirmModal';
import ToastStack from '../components/Toast';
import { useToast } from '../hooks/useToast';

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-CA', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

type Action =
  | { type: 'approve'; offer: PendingOffer }
  | { type: 'refuse'; offer: PendingOffer };

export default function Offers({
  onCountChange,
}: {
  onCountChange?: (n: number) => void;
}) {
  const [offers, setOffers] = useState<PendingOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [pending, setPending] = useState<Action | null>(null);
  const { toasts, push, dismiss } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    api
      .getPendingOffers()
      .then((r) => {
        setOffers(r.data);
        onCountChange?.(r.data.length);
      })
      .catch(() => push('Impossible de charger les offres.', 'error'))
      .finally(() => setLoading(false));
  }, [onCountChange, push]);

  useEffect(() => {
    load();
  }, [load]);

  async function approve(offer: PendingOffer) {
    setActionLoading(offer.id);
    try {
      await api.approveOffer(offer.id);
      push(`"${offer.title}" approuvée — offre maintenant active.`, 'success');
      setOffers((prev) => prev.filter((o) => o.id !== offer.id));
      onCountChange?.(offers.length - 1);
    } catch (err: unknown) {
      push(err instanceof Error ? err.message : 'Erreur.', 'error');
    } finally {
      setActionLoading(null);
      setPending(null);
    }
  }

  async function refuse(offer: PendingOffer, reason: string) {
    setActionLoading(offer.id);
    try {
      await api.refuseOffer(offer.id, reason);
      push(`"${offer.title}" refusée.`, 'success');
      setOffers((prev) => prev.filter((o) => o.id !== offer.id));
      onCountChange?.(offers.length - 1);
    } catch (err: unknown) {
      push(err instanceof Error ? err.message : 'Erreur.', 'error');
    } finally {
      setActionLoading(null);
      setPending(null);
    }
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <>
      <ToastStack toasts={toasts} dismiss={dismiss} />

      <div className="section-head">
        <div>
          <div className="section-title">Anciennes offres en attente</div>
          <div
            style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}
          >
            File de transition seulement. Les nouvelles offres Hôte sont
            publiées directement sur le LIVE.
          </div>
        </div>
        <div className="section-count">
          {offers.length} offre{offers.length !== 1 ? 's' : ''}
        </div>
      </div>

      {offers.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">✓</div>
          <div className="empty-title">Aucune offre en attente</div>
          <div className="empty-sub">La file héritée est vide.</div>
        </div>
      ) : (
        <div className="offer-grid">
          {offers.map((offer) => (
            <OfferCard
              key={offer.id}
              offer={offer}
              busy={actionLoading === offer.id}
              onApprove={() => setPending({ type: 'approve', offer })}
              onRefuse={() => setPending({ type: 'refuse', offer })}
            />
          ))}
        </div>
      )}

      {pending?.type === 'approve' && (
        <ConfirmModal
          title="Approuver l'offre"
          description={`"${pending.offer.title}" deviendra immédiatement active sur le feed live.`}
          confirmLabel="Approuver"
          confirmVariant="success"
          onConfirm={() => approve(pending.offer)}
          onCancel={() => setPending(null)}
          loading={actionLoading === pending.offer.id}
        />
      )}

      {pending?.type === 'refuse' && (
        <RefuseModal
          offerTitle={pending.offer.title}
          onConfirm={(reason) => refuse(pending.offer, reason)}
          onCancel={() => setPending(null)}
          loading={actionLoading === pending.offer.id}
        />
      )}
    </>
  );
}

interface OfferCardProps {
  offer: PendingOffer;
  busy: boolean;
  onApprove: () => void;
  onRefuse: () => void;
}

function OfferCard({ offer, busy, onApprove, onRefuse }: OfferCardProps) {
  return (
    <div className="offer-card">
      {offer.photo_url ? (
        <img src={offer.photo_url} alt={offer.title} className="offer-photo" />
      ) : (
        <div className="offer-photo-placeholder">🏠</div>
      )}

      <div className="offer-body">
        <div className="offer-title">{offer.title}</div>
        <div className="offer-price">{formatPrice(offer.reference_price)}</div>
        <div className="offer-meta">
          <div className="offer-meta-row">
            <span className="icon">◎</span>
            {offer.location_label}
          </div>
          <div className="offer-meta-row">
            <span className="icon">◷</span>
            Séjour {formatDate(offer.starts_at)} →{' '}
            {formatDate(offer.checkout_at)}
          </div>
          <div className="offer-meta-row">
            <span className="icon">◷</span>
            Réservable jusqu’au {formatDate(offer.expires_at)}
          </div>
          <div className="offer-meta-row">
            <span className="icon">◷</span>
            Soumis {formatDate(offer.created_at)}
          </div>
        </div>
        {offer.description && (
          <div className="offer-desc">{offer.description}</div>
        )}
        <div className="offer-host">
          <span
            style={{ color: 'var(--text-dim)', fontSize: 10, marginRight: 4 }}
          >
            Hôte
          </span>
          {offer.host_id}
        </div>
      </div>

      <div className="offer-actions">
        <button
          className="btn btn-success"
          style={{ flex: 1, justifyContent: 'center' }}
          disabled={busy}
          onClick={onApprove}
        >
          {busy ? <span className="spinner" /> : '✓'}
          Approuver
        </button>
        <button
          className="btn btn-danger"
          style={{ flex: 1, justifyContent: 'center' }}
          disabled={busy}
          onClick={onRefuse}
        >
          ✕ Refuser
        </button>
      </div>
    </div>
  );
}
